import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Save, Upload, X, Image as ImageIcon, AlertTriangle, Trash2, Palette, FileText, Eye, Download, DatabaseBackup } from "lucide-react";

const empty = {
  company_name: "",
  address: "",
  phone: "",
  email: "",
  tax_id: "",
  logo_data_url: "",
  footer_note: "",
  pdf_template: "minimal",
  pdf_accent_color: "#09090B",
  pdf_header_title: "",
  pdf_header_subtitle: "",
  payment_options: [],
  product_categories: [],
  custom_xlsx_template_b64: "",
  custom_xlsx_template_name: "",
  custom_report_xlsx_template_b64: "",
  custom_report_xlsx_template_name: "",
  confetti_threshold: 0,
};

export default function Settings() {
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetConfirm, setResetConfirm] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [fullResetOpen, setFullResetOpen] = useState(false);
  const [fullResetConfirm, setFullResetConfirm] = useState("");
  const [fullResetBusy, setFullResetBusy] = useState(false);
  const fileRef = useRef(null);
  const restoreRef = useRef(null);
  const [backupBusy, setBackupBusy] = useState(false);
  const [restoreBusy, setRestoreBusy] = useState(false);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoreConfirm, setRestoreConfirm] = useState("");

  const doBackup = async () => {
    setBackupBusy(true);
    try {
      const res = await api.get("/settings/backup", { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      a.href = url; a.download = `yedek_${stamp}.json`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Yedek dosyası indiriliyor");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBackupBusy(false); }
  };

  const openRestore = (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json")) {
      toast.error("Sadece .json yedek dosyaları yüklenebilir");
      return;
    }
    setRestoreFile(file);
    setRestoreConfirm("");
    setRestoreOpen(true);
    if (restoreRef.current) restoreRef.current.value = "";
  };

  const doRestore = async () => {
    if (!restoreFile) return;
    if (restoreConfirm !== "GERI YUKLE") {
      toast.error("Onay metni 'GERI YUKLE' olmalı");
      return;
    }
    setRestoreBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const { data } = await api.post("/settings/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      const total = Object.values(data.restored || {}).reduce((s, n) => s + n, 0);
      toast.success(`Sistem geri yüklendi (${total} kayıt)`);
      setRestoreOpen(false);
      setRestoreFile(null);
      setRestoreConfirm("");
      setTimeout(() => window.location.reload(), 800);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setRestoreBusy(false); }
  };

  const load = async () => {
    try { const { data } = await api.get("/settings"); setForm({ ...empty, ...data }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const onLogo = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Sadece görsel dosya yüklenebilir"); return; }
    if (file.size > 500_000) { toast.error("Logo 500 KB'dan büyük olmamalı"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, logo_data_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const clearLogo = () => {
    setForm((f) => ({ ...f, logo_data_url: "" }));
    if (fileRef.current) fileRef.current.value = "";
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings", form);
      toast.success("Ayarlar kaydedildi");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const doReset = async () => {
    if (resetConfirm !== "SIFIRLA") {
      toast.error("Onay metni 'SIFIRLA' olmalı");
      return;
    }
    setResetBusy(true);
    try {
      const { data } = await api.post("/settings/factory-reset", { confirm: resetConfirm });
      const total = Object.values(data.cleared || {}).reduce((s, n) => s + n, 0);
      toast.success(`Tüm veriler sıfırlandı (${total} kayıt silindi)`);
      setResetOpen(false);
      setResetConfirm("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setResetBusy(false); }
  };

  const doFullReset = async () => {
    if (fullResetConfirm !== "SIFIRLA HEPSI") {
      toast.error("Onay metni 'SIFIRLA HEPSI' olmalı");
      return;
    }
    setFullResetBusy(true);
    try {
      const { data } = await api.post("/settings/full-reset", { confirm: "SIFIRLA HEPSI" });
      const totals = Object.entries(data.cleared || {}).map(([k, v]) => `${k}: ${v}`).join(", ");
      toast.success(`Tüm sistem sıfırlandı — ${totals}. Yeniden giriş yapmanız gerekiyor.`);
      setFullResetOpen(false);
      setFullResetConfirm("");
      // Session is stale after we wiped the user table; force logout.
      setTimeout(() => { window.location.href = "/giris"; }, 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setFullResetBusy(false); }
  };


  return (
    <div className="space-y-6" data-testid="settings-page">
      <div className="flex items-end justify-between border-b-2 border-[#09090B] pb-4">
        <div>
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-neutral-500">Firma / PDF Antet</div>
          <h1 className="text-4xl font-black tracking-tighter font-heading">Ayarlar</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Bu bilgiler; sipariş, irsaliye ve günlük rapor PDF'lerinin başlığında görünür.
          </p>
        </div>
        <Button
          onClick={save} disabled={busy}
          className="rounded-none bg-[#09090B] hover:bg-[#0055FF]"
          data-testid="save-settings-btn"
        >
          <Save size={16} className="mr-2" /> {busy ? "Kaydediliyor…" : "Kaydet"}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Company info */}
        <div className="lg:col-span-2 bg-white border-2 border-[#09090B] p-6 space-y-4">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Firma Bilgileri</div>

          <div>
            <Label className="text-xs font-mono uppercase">Firma Adı</Label>
            <Input value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value })}
              className="rounded-none border-2 border-[#09090B] h-11 focus-visible:ring-0"
              data-testid="company-name-input" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-mono uppercase">Telefon</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="rounded-none border-2 border-[#09090B] font-mono focus-visible:ring-0"
                data-testid="company-phone-input" />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">E-posta</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="rounded-none border-2 border-[#09090B] font-mono focus-visible:ring-0"
                data-testid="company-email-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs font-mono uppercase">Vergi No / Vergi Dairesi</Label>
            <Input value={form.tax_id} onChange={(e) => setForm({ ...form, tax_id: e.target.value })}
              className="rounded-none border-2 border-[#09090B] font-mono focus-visible:ring-0"
              data-testid="company-tax-input" />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase">Adres</Label>
            <Textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })}
              rows={3} className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
              data-testid="company-address-input" />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase">PDF Dipnot (opsiyonel)</Label>
            <Textarea value={form.footer_note} onChange={(e) => setForm({ ...form, footer_note: e.target.value })}
              rows={2} placeholder="Örn. IBAN, teşekkür notu, garanti şartları…"
              className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
              data-testid="company-footer-input" />
          </div>

          <div>
            <Label className="text-xs font-mono uppercase">Konfeti Eşiği (₺)</Label>
            <Input
              type="number"
              min="0"
              step="1000"
              value={form.confetti_threshold ?? 0}
              onChange={(e) => setForm({ ...form, confetti_threshold: parseFloat(e.target.value) || 0 })}
              placeholder="Örn. 1000000"
              className="rounded-none border-2 border-[#09090B] font-mono focus-visible:ring-0"
              data-testid="confetti-threshold-input"
            />
            <div className="mt-1 text-[10px] font-mono text-neutral-500">
              Günlük KDV hariç satış her bu tutarın katına ulaştığında (₺{Number(form.confetti_threshold || 0).toLocaleString("tr-TR")}, ₺{(Number(form.confetti_threshold || 0) * 2).toLocaleString("tr-TR")}, ₺{(Number(form.confetti_threshold || 0) * 3).toLocaleString("tr-TR")} vb.) tüm aktif kullanıcılarda konfeti patlar. 0 girerseniz kapalıdır.
            </div>
          </div>
        </div>

        {/* Logo */}
        <div className="bg-white border-2 border-[#09090B] p-6 space-y-4">
          <div className="text-xs font-mono uppercase tracking-widest text-neutral-500">Logo</div>

          <div className="border-2 border-dashed border-[#09090B] h-40 flex items-center justify-center bg-neutral-50">
            {form.logo_data_url ? (
              <img
                src={form.logo_data_url}
                alt="Logo önizleme"
                className="max-h-32 max-w-full object-contain"
                data-testid="logo-preview"
              />
            ) : (
              <div className="text-center text-neutral-400 text-xs font-mono uppercase tracking-widest">
                <ImageIcon size={28} className="mx-auto mb-2" />
                Logo yok
              </div>
            )}
          </div>

          <input
            ref={fileRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={(e) => onLogo(e.target.files?.[0])}
            className="block w-full text-sm font-mono file:mr-3 file:py-2 file:px-3 file:border-2 file:border-[#09090B] file:bg-white file:text-[#09090B] file:font-bold file:uppercase file:text-xs file:tracking-widest hover:file:bg-[#09090B] hover:file:text-white"
            data-testid="logo-file-input"
          />

          {form.logo_data_url && (
            <Button
              variant="outline" size="sm"
              onClick={clearLogo}
              className="rounded-none border-2 border-[#09090B] w-full"
              data-testid="clear-logo-btn"
            >
              <X size={14} className="mr-2" /> Logoyu Kaldır
            </Button>
          )}

          <div className="text-xs font-mono text-neutral-500 leading-relaxed">
            PNG / JPG / WebP · Max 500 KB · Şeffaf arka plan önerilir.
          </div>
        </div>
      </div>

      {/* Live preview of PDF header */}
      <div className="bg-white border-2 border-[#09090B] p-6">
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-3">PDF Antet Önizleme</div>
        <div className="border-b border-black pb-4 flex items-start gap-6">
          {form.logo_data_url ? (
            <img src={form.logo_data_url} alt="" className="h-16 object-contain" />
          ) : (
            <div className="h-16 w-24 border-2 border-dashed border-neutral-300 flex items-center justify-center text-[10px] font-mono text-neutral-400">LOGO</div>
          )}
          <div className="flex-1 text-right space-y-0.5">
            <div className="font-bold text-lg font-heading">{form.company_name || "Firma Adı"}</div>
            {form.address && <div className="text-sm text-neutral-700 whitespace-pre-line">{form.address}</div>}
            <div className="text-xs font-mono text-neutral-600">
              {[form.phone, form.email].filter(Boolean).join(" · ")}
            </div>
            {form.tax_id && <div className="text-xs font-mono text-neutral-600">Vergi No: {form.tax_id}</div>}
          </div>
        </div>
        <div className="mt-3 text-xs font-mono text-neutral-500 italic">
          (Örnek belge bloğu buraya gelecek…)
        </div>
        {form.footer_note && (
          <div className="mt-8 pt-3 border-t border-neutral-300 text-xs text-neutral-500 whitespace-pre-line">
            {form.footer_note}
          </div>
        )}
      </div>

      {/* PDF Template editor — admin only. These fields flow directly into
          every generated PDF (order, waybill, daily report). */}
      <div className="bg-white border-2 border-[#0055FF] p-6 space-y-4" data-testid="pdf-template-editor">
        <div className="flex items-center gap-3">
          <FileText size={22} className="text-[#0055FF]" />
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#0055FF]">Yönetici · Dinamik Şablon</div>
            <h2 className="text-2xl font-black tracking-tighter font-heading">PDF Şablonu Düzenle</h2>
          </div>
        </div>
        <p className="text-sm text-neutral-700 leading-relaxed">
          Buradaki değişiklikler <strong>tüm PDF çıktılarında</strong> (sipariş, irsaliye, günlük rapor) anında yansır.
          Logo ve firma bilgileri yukarıda; alt not aşağıda; üst başlık ise buradan yönetilir.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-mono uppercase">PDF Üst Başlığı</Label>
            <Input
              value={form.pdf_header_title || ""}
              onChange={(e) => setForm({ ...form, pdf_header_title: e.target.value })}
              placeholder="Örn: HAKPİ ERP · Resmi Belge"
              className="rounded-none border-2 border-[#09090B] h-11 focus-visible:ring-0"
              data-testid="pdf-header-title-input"
            />
            <div className="text-[10px] font-mono text-neutral-500 mt-1">
              Boşsa yalnızca belge başlığı (SİPARİŞ, İRSALİYE, RAPOR) gösterilir.
            </div>
          </div>
          <div>
            <Label className="text-xs font-mono uppercase">PDF Alt Başlık (Küçük)</Label>
            <Input
              value={form.pdf_header_subtitle || ""}
              onChange={(e) => setForm({ ...form, pdf_header_subtitle: e.target.value })}
              placeholder="Örn: 2026 Belge Serisi · Onaylı"
              className="rounded-none border-2 border-[#09090B] h-11 focus-visible:ring-0"
              data-testid="pdf-header-subtitle-input"
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2 border-t border-neutral-200 text-xs font-mono">
          <div>
            <div className="uppercase text-neutral-500">Logo</div>
            <div className="mt-1 text-neutral-800">
              {form.logo_data_url ? "Yüklü" : "—"} · yukarıdaki Logo kartından değiştirin
            </div>
          </div>
          <div>
            <div className="uppercase text-neutral-500">Alt Metin (Footer)</div>
            <div className="mt-1 text-neutral-800 truncate">
              {form.footer_note ? form.footer_note.slice(0, 40) + (form.footer_note.length > 40 ? "…" : "") : "—"} · Firma kartından
            </div>
          </div>
          <div>
            <div className="uppercase text-neutral-500">Şablon Stili</div>
            <div className="mt-1 text-neutral-800">{form.pdf_template || "minimal"}</div>
          </div>
        </div>

        <div className="text-xs font-mono text-neutral-500 leading-relaxed pt-2 border-t border-neutral-200">
          Sipariş ve irsaliye PDF'lerinde <strong>Birim Fiyat</strong> ve <strong>Satır Toplamı</strong> sütunları
          otomatik gösterilir. Günlük rapor (detaylı) PDF'i de aynı bilgileri içerir.
        </div>
      </div>

      <div className="bg-white border-2 border-[#09090B] p-6 space-y-4" data-testid="waybill-xlsx-columns-section">
        <div className="flex items-center gap-3">
          <FileText size={20} />
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-neutral-500">İrsaliye</div>
            <h2 className="text-2xl font-black tracking-tighter font-heading">Excel Şablonu (Kolonlar)</h2>
          </div>
        </div>
        <p className="text-sm text-neutral-700 leading-relaxed">
          Muhasebe ekranından irsaliye Excel dosyası indirildiğinde hangi kolonların dahil edileceğini buradan seçin.
        </p>
        <WaybillXlsxColumnsEditor />
      </div>

      {/* Payment options — dynamic combobox source for Quote form */}
      <div className="bg-white border-2 border-[#09090B] p-6 space-y-4" data-testid="payment-options-section">
        <div className="flex items-center gap-3">
          <FileText size={20} />
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-neutral-500">Teklif / Sipariş</div>
            <h2 className="text-2xl font-black tracking-tighter font-heading">Ödeme Bilgisi Seçenekleri</h2>
          </div>
        </div>
        <p className="text-sm text-neutral-700 leading-relaxed">
          Burada tanımladığınız seçenekler, yeni teklif oluşturma ekranındaki <strong>Ödeme Bilgisi</strong> kutusunda otomatik olarak seçilebilir hâlde görünür.
        </p>
        <PaymentOptionsEditor
          items={form.payment_options || []}
          onChange={(next) => setForm({ ...form, payment_options: next })}
        />
        <div className="text-[10px] font-mono text-neutral-500">
          Kaydetmeyi unutmayın — değişiklikler <em>Kaydet</em>'e bastığınızda geçerli olur.
        </div>
      </div>

      {/* Product categories — dynamic list for Products page */}
      <div className="bg-white border-2 border-[#09090B] p-6 space-y-4" data-testid="product-categories-section">
        <div className="flex items-center gap-3">
          <FileText size={20} />
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-neutral-500">Katalog</div>
            <h2 className="text-2xl font-black tracking-tighter font-heading">Ürün Kategorileri</h2>
          </div>
        </div>
        <p className="text-sm text-neutral-700 leading-relaxed">
          Burada tanımladığınız kategoriler, Ürün ekranındaki <strong>Kategori</strong> seçicisinde ve liste filtresinde kullanılır.
        </p>
        <PaymentOptionsEditor
          items={form.product_categories || []}
          onChange={(next) => setForm({ ...form, product_categories: next })}
        />
        <div className="text-[10px] font-mono text-neutral-500">
          Kaydetmeyi unutmayın — değişiklikler <em>Kaydet</em>'e bastığınızda geçerli olur.
        </div>
      </div>

      {/* Excel PDF Template */}
      <div className="bg-white border-2 border-[#09090B] p-6 space-y-4" data-testid="excel-template-section">
        <div className="flex items-center gap-3">
          <FileText size={20} />
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-neutral-500">Belge Şablonu</div>
            <h2 className="text-2xl font-black tracking-tighter font-heading">Excel PDF Şablonu</h2>
          </div>
        </div>

        <div className="text-sm text-neutral-700 space-y-2 leading-relaxed">
          <p>
            Sipariş PDF'i için kendi Excel şablonunuzu yükleyebilirsiniz. Şablonda köşeli çift parantezli
            <span className="font-mono bg-neutral-100 px-1"> {"{{yer_tutucu}}"} </span> ifadeleri sipariş verileriyle otomatik doldurulur.
          </p>
          <p>
            <strong>Kullanılabilir alanlar</strong>:
            <span className="font-mono text-xs bg-neutral-100 px-1 ml-1">
              {"{{logo}}"} {"{{order_number}}"} {"{{quote_number}}"} {"{{customer_name}}"} {"{{created_at}}"} {"{{notes}}"}
              {" · "}{"{{created_by_name}}"} {"{{customer_address}}"} {"{{customer_phone}}"} {"{{customer_email}}"} {"{{customer_tax_id}}"}
              {" · "}{"{{subtotal}}"} {"{{tax_rate}}"} {"{{tax_amount}}"} {"{{total}}"} {"{{currency}}"}
              {" · "}{"{{company_name}}"} {"{{company_address}}"} {"{{company_phone}}"} {"{{company_email}}"} {"{{company_tax_id}}"}
            </span>
          </p>
          <p>
            <strong>Kalem satırı</strong>: Bir satıra
            <span className="font-mono bg-neutral-100 px-1"> {"{{item.product_name}}"} </span>
            gibi <em>item.*</em> yer tutucu yazın; bu satır sipariş kalem sayısı kadar otomatik çoğaltılır.
            <br />Kullanılabilir alanlar: <span className="font-mono text-xs">
              {"{{item.sku}}"} {"{{item.product_name}}"} {"{{item.unit}}"} {"{{item.quantity}}"} {"{{item.sevk}}"} {"{{item.unit_price}}"} {"{{item.line_total}}"} {"{{item.due_date}}"} {"{{item.termin}}"} {"{{item.notes}}"} {"{{item.aciklama}}"}
            </span>
          </p>
          <p className="text-xs text-neutral-500">
            <strong>{"{{logo}}"}</strong> yer tutucusunun bulunduğu hücreye <em>Ayarlar → Logo</em> alanına yüklediğiniz firma logosu otomatik yerleştirilir. <strong>{"{{item.sevk}}"}</strong> "Depo" veya "Uretim" olarak sevk rotasını yazar.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="rounded-none border-2 border-[#09090B]"
            onClick={async () => {
              const res = await api.get("/settings/pdf-template-sample", { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url; a.download = "siparis_sablon_ornek.xlsx"; a.click();
              URL.revokeObjectURL(url);
            }}
            data-testid="download-sample-btn"
          >
            <Upload size={16} className="mr-2 rotate-180" /> Örnek Şablonu İndir
          </Button>

          <input
            id="xlsx-upload"
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            data-testid="xlsx-upload-input"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const fd = new FormData();
              fd.append("file", file);
              try {
                await api.post("/settings/pdf-template-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                toast.success(`Şablon yüklendi: ${file.name}`);
                load();
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail));
              }
              e.target.value = "";
            }}
          />
          <Button
            className="rounded-none bg-[#09090B] hover:bg-[#0055FF]"
            onClick={() => document.getElementById("xlsx-upload")?.click()}
            data-testid="xlsx-upload-btn"
          >
            <Upload size={16} className="mr-2" /> Şablon Yükle (.xlsx)
          </Button>

          {form.custom_xlsx_template_name && (
            <Button
              variant="outline"
              className="rounded-none border-2 border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white"
              onClick={async () => {
                try {
                  const res = await api.get("/settings/pdf-template-test", { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const win = window.open(url, "_blank");
                  if (!win) {
                    const a = document.createElement("a");
                    a.href = url; a.target = "_blank"; a.rel = "noopener";
                    document.body.appendChild(a); a.click(); a.remove();
                  }
                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                  toast.success("Şablon geçerli — örnek PDF yeni sekmede açıldı");
                } catch (err) {
                  // Backend returns 400 with detailed template error message
                  const raw = err.response?.data;
                  let detail = "Şablonda hata var";
                  if (raw instanceof Blob) {
                    try { detail = JSON.parse(await raw.text()).detail || detail; } catch { /* keep default */ }
                  } else if (raw?.detail) {
                    detail = raw.detail;
                  }
                  toast.error(detail);
                }
              }}
              data-testid="test-template-btn"
            >
              <FileText size={16} className="mr-2" /> Şablonu Test Et
            </Button>
          )}

          {form.custom_xlsx_template_name && (
            <Button
              variant="outline"
              className="rounded-none border-2 border-[#09090B]"
              onClick={async () => {
                const res = await api.get("/settings/pdf-template-current", { responseType: "blob" });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement("a");
                a.href = url; a.download = form.custom_xlsx_template_name; a.click();
                URL.revokeObjectURL(url);
              }}
              data-testid="download-current-btn"
            >
              <FileText size={16} className="mr-2" /> Mevcut Şablonu İndir
            </Button>
          )}

          {form.custom_xlsx_template_name && (
            <Button
              variant="outline"
              className="rounded-none border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"
              onClick={async () => {
                if (!window.confirm("Yüklü şablonu kaldırıp yerleşik tasarıma dönmek istiyor musunuz?")) return;
                await api.delete("/settings/pdf-template-upload");
                toast.success("Özel şablon kaldırıldı, yerleşik tasarım kullanılacak");
                load();
              }}
              data-testid="delete-xlsx-btn"
            >
              <Trash2 size={16} className="mr-2" /> Şablonu Kaldır
            </Button>
          )}
        </div>

        <div className="border-t border-neutral-200 pt-3 flex items-center gap-2 text-sm font-mono">
          <span className="text-neutral-500 uppercase tracking-widest text-xs">Aktif:</span>
          {form.custom_xlsx_template_name ? (
            <span className="inline-flex items-center gap-2 px-2 py-1 border-2 border-[#00A650] text-[#00A650]" data-testid="active-template-name">
              {form.custom_xlsx_template_name}
            </span>
          ) : (
            <span className="text-neutral-500" data-testid="active-template-name">Yerleşik tasarım (özel şablon yok)</span>
          )}
        </div>
      </div>
      {/* Excel Report Template — dedicated to /reports/daily/pdf output */}
      <div className="bg-white border-2 border-[#09090B] p-6 space-y-4" data-testid="report-template-section">
        <div className="flex items-center gap-3">
          <FileText size={20} />
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-neutral-500">Rapor · Dinamik</div>
            <h2 className="text-2xl font-black tracking-tighter font-heading">Rapor Şablonu (Excel)</h2>
          </div>
        </div>

        <div className="text-sm text-neutral-700 space-y-2 leading-relaxed">
          <p>
            <strong>Günlük Sipariş Raporu</strong> PDF'i, yüklediğiniz .xlsx şablonuna göre üretilir. LibreOffice ile PDF'e dönüştürülür. Şablon yoksa yerleşik tasarım kullanılır.
          </p>
          <p>
            <strong>Kalem satırı</strong>: Şablonda <span className="font-mono bg-neutral-100 px-1"> {"{{item.*}}"} </span> yer tutucusu içeren satır, rapordaki her sipariş kalemi için otomatik çoğaltılır. Kullanılabilir alanlar:
            <span className="font-mono text-xs bg-neutral-100 px-1 ml-1 block mt-1">
              {"{{item.order_number}}"} {"{{item.order_date}}"} {"{{item.customer}}"} {"{{item.sku}}"} {"{{item.product_name}}"} {"{{item.unit}}"} {"{{item.quantity}}"} {"{{item.unit_price}}"} {"{{item.line_total}}"} {"{{item.allocation}}"} {"{{item.creator}}"}
            </span>
          </p>
          <p>
            <strong>Sayfa alanları</strong>:
            <span className="font-mono text-xs bg-neutral-100 px-1 ml-1">
              {"{{logo}}"} {"{{report_title}}"} {"{{date_range}}"} {"{{generated_at}}"} {"{{total_orders}}"} {"{{grand_total}}"} {"{{subtotal_sum}}"} {"{{tax_sum}}"} {"{{company_name}}"} {"{{currency}}"}
            </span>
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="rounded-none border-2 border-[#09090B]"
            onClick={async () => {
              const res = await api.get("/settings/report-template-sample", { responseType: "blob" });
              const url = URL.createObjectURL(res.data);
              const a = document.createElement("a");
              a.href = url; a.download = "rapor_sablon_ornek.xlsx"; a.click();
              URL.revokeObjectURL(url);
            }}
            data-testid="download-report-sample-btn"
          >
            <Upload size={16} className="mr-2 rotate-180" /> Örnek Rapor Şablonu
          </Button>

          <input
            id="report-xlsx-upload"
            type="file"
            accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            data-testid="report-xlsx-upload-input"
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const fd = new FormData();
              fd.append("file", file);
              try {
                await api.post("/settings/report-template-upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
                toast.success(`Rapor şablonu yüklendi: ${file.name}`);
                load();
              } catch (err) {
                toast.error(formatApiErrorDetail(err.response?.data?.detail));
              }
              e.target.value = "";
            }}
          />
          <Button
            className="rounded-none bg-[#09090B] hover:bg-[#0055FF]"
            onClick={() => document.getElementById("report-xlsx-upload")?.click()}
            data-testid="report-xlsx-upload-btn"
          >
            <Upload size={16} className="mr-2" /> Rapor Şablonu Yükle (.xlsx)
          </Button>

          {form.custom_report_xlsx_template_name && (
            <Button
              variant="outline"
              className="rounded-none border-2 border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white"
              onClick={async () => {
                try {
                  const res = await api.get("/settings/report-template-test", { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  window.open(url, "_blank");
                  setTimeout(() => URL.revokeObjectURL(url), 60_000);
                  toast.success("Rapor şablonu geçerli — örnek PDF yeni sekmede");
                } catch (err) {
                  const raw = err.response?.data;
                  let detail = "Şablonda hata var";
                  if (raw instanceof Blob) {
                    try { detail = JSON.parse(await raw.text()).detail || detail; } catch { /* keep */ }
                  } else if (raw?.detail) detail = raw.detail;
                  toast.error(detail);
                }
              }}
              data-testid="test-report-template-btn"
            >
              <FileText size={16} className="mr-2" /> Şablonu Test Et
            </Button>
          )}

          {form.custom_report_xlsx_template_name && (
            <Button
              variant="outline"
              className="rounded-none border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"
              onClick={async () => {
                if (!window.confirm("Yüklü rapor şablonunu kaldırmak istiyor musunuz?")) return;
                await api.delete("/settings/report-template-upload");
                toast.success("Rapor şablonu kaldırıldı, yerleşik tasarım kullanılacak");
                load();
              }}
              data-testid="delete-report-xlsx-btn"
            >
              <Trash2 size={16} className="mr-2" /> Şablonu Kaldır
            </Button>
          )}
        </div>

        <div className="border-t border-neutral-200 pt-3 flex items-center gap-2 text-sm font-mono">
          <span className="text-neutral-500 uppercase tracking-widest text-xs">Aktif:</span>
          {form.custom_report_xlsx_template_name ? (
            <span className="inline-flex items-center gap-2 px-2 py-1 border-2 border-[#00A650] text-[#00A650]" data-testid="active-report-template-name">
              {form.custom_report_xlsx_template_name}
            </span>
          ) : (
            <span className="text-neutral-500" data-testid="active-report-template-name">Yerleşik reportlab tasarımı (özel şablon yok)</span>
          )}
        </div>
      </div>
      <div className="bg-white border-2 border-[#0055FF] p-6 space-y-4" data-testid="auto-backup-section">
        <div className="flex items-center gap-3">
          <DatabaseBackup size={22} className="text-[#0055FF]" />
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#0055FF]">Otomatik Yedekleme</div>
            <h2 className="text-2xl font-black tracking-tighter font-heading">Günlük E-posta ile Yedek</h2>
          </div>
        </div>
        <p className="text-sm text-neutral-700 leading-relaxed">
          Belirlediğin saatte sistemin tam JSON yedeği <strong>belirtilen e-posta adresine</strong> otomatik gönderilir.
          20 MB üstündeki yedeklerde sadece istatistik + hatırlatma maili gider.
        </p>
        <AutoBackupBlock />
      </div>

      <div className="bg-white border-2 border-[#0055FF] p-6 space-y-4" data-testid="auto-daily-report-section">
        <div className="flex items-center gap-3">
          <FileText size={22} className="text-[#0055FF]" />
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#0055FF]">Otomatik Günlük Rapor</div>
            <h2 className="text-2xl font-black tracking-tighter font-heading">Günlük PDF Raporu E-postası</h2>
          </div>
        </div>
        <p className="text-sm text-neutral-700 leading-relaxed">
          Belirlediğin saatte, o günkü <strong>detaylı sipariş raporu PDF'i</strong> belirtilen e-posta adresine otomatik olarak gönderilir.
          Yönetici / alt-yönetici tarafından yapılandırılabilir.
        </p>
        <AutoDailyReportBlock />
      </div>

      <div className="bg-white border-2 border-[#0055FF] p-6 space-y-4" data-testid="backup-section">
        <div className="flex items-center gap-3">
          <DatabaseBackup size={22} className="text-[#0055FF]" />
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#0055FF]">Sistem Yedeği</div>
            <h2 className="text-2xl font-black tracking-tighter font-heading">Yedekle & Geri Yükle</h2>
          </div>
        </div>
        <p className="text-sm text-neutral-700 leading-relaxed">
          Kullanıcılar, müşteriler, ürünler, teklifler, siparişler, irsaliyeler ve firma ayarları dahil
          <strong> tüm sistem verisini tek bir JSON dosyası</strong> olarak bilgisayarınıza indirebilir; ihtiyaç
          duyduğunuzda aynı dosyayı yükleyerek sistemi <strong>o günkü haline geri döndürebilirsiniz</strong>.
          Geri yükleme mevcut verinin tamamının üzerine yazar.
        </p>

        <div className="flex flex-wrap gap-2">
          <Button
            className="rounded-none bg-[#0055FF] hover:bg-[#09090B] text-white"
            onClick={doBackup}
            disabled={backupBusy}
            data-testid="backup-download-btn"
          >
            <Download size={16} className="mr-2" />
            {backupBusy ? "Hazırlanıyor…" : "Yedek İndir (.json)"}
          </Button>

          <input
            ref={restoreRef}
            id="restore-upload"
            type="file"
            accept=".json,application/json"
            className="hidden"
            data-testid="restore-file-input"
            onChange={(e) => openRestore(e.target.files?.[0])}
          />
          <Button
            variant="outline"
            className="rounded-none border-2 border-[#0055FF] text-[#0055FF] hover:bg-[#0055FF] hover:text-white"
            onClick={() => restoreRef.current?.click()}
            data-testid="restore-upload-btn"
          >
            <Upload size={16} className="mr-2" /> Yedekten Geri Yükle
          </Button>
        </div>

        <div className="text-xs font-mono text-neutral-500 leading-relaxed">
          Yedek dosyası kullanıcı şifre özetlerini de içerir. Dosyayı güvenli bir yerde saklayın.
        </div>
      </div>

      <div className="bg-white border-2 border-[#FF3B30] p-6 space-y-4" data-testid="danger-zone">
        <div className="flex items-center gap-3">
          <AlertTriangle size={22} className="text-[#FF3B30]" />
          <div>
            <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#FF3B30]">Tehlikeli Alan</div>
            <h2 className="text-2xl font-black tracking-tighter font-heading">Tüm Verileri Sıfırla</h2>
          </div>
        </div>
        <p className="text-sm text-neutral-700 leading-relaxed">
          Bu işlem <strong>tüm müşterileri, ürünleri, teklifleri, siparişleri, üretim emirlerini ve irsaliyeleri
          kalıcı olarak siler</strong>. Kullanıcı hesapları, roller ve firma ayarları etkilenmez. Bu işlem <strong>geri alınamaz</strong>.
        </p>
        <Button
          variant="outline"
          className="rounded-none border-2 border-[#FF3B30] text-[#FF3B30] hover:bg-[#FF3B30] hover:text-white"
          onClick={() => { setResetConfirm(""); setResetOpen(true); }}
          data-testid="open-reset-btn"
        >
          <Trash2 size={16} className="mr-2" /> Tüm Verileri Sıfırla
        </Button>

        <div className="pt-4 mt-2 border-t-2 border-[#FF3B30]/40">
          <div className="text-xs font-mono uppercase tracking-[0.3em] text-[#FF3B30] mb-1">Nükleer Sıfırlama</div>
          <p className="text-sm text-neutral-700 leading-relaxed mb-3">
            Bu işlem <strong>kullanıcı hesapları, firma ayarları, logo ve PDF şablonu dahil</strong> tüm veriyi siler,
            sadece 5 varsayılan hesabı yeniden oluşturur (admin, satış, depo, üretim, muhasebe — hepsi varsayılan
            şifreleriyle). <strong>Production'da temiz başlangıç için tasarlandı.</strong> Kesinlikle geri alınamaz.
          </p>
          <Button
            variant="outline"
            className="rounded-none border-2 border-[#7C0002] text-[#7C0002] hover:bg-[#7C0002] hover:text-white font-bold"
            onClick={() => { setFullResetConfirm(""); setFullResetOpen(true); }}
            data-testid="open-full-reset-btn"
          >
            <AlertTriangle size={16} className="mr-2" /> Tüm Sistemi Sıfırla (Nükleer)
          </Button>
        </div>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent className="rounded-none border-2 border-[#FF3B30] bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2 text-[#FF3B30]">
              <AlertTriangle size={22} /> Onay Gerekli
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">
              Silinecek koleksiyonlar: <span className="font-mono">müşteriler, ürünler, teklifler, siparişler, irsaliyeler, sayaçlar</span>. Bu işlem geri alınamaz.
            </p>
            <div>
              <Label className="text-xs font-mono uppercase">Onaylamak için <span className="text-[#FF3B30] font-bold">SIFIRLA</span> yazın</Label>
              <Input
                value={resetConfirm}
                onChange={(e) => setResetConfirm(
                  e.target.value.toLocaleUpperCase("tr-TR").replace(/İ/g, "I").replace(/Ç/g, "C").replace(/Ğ/g, "G").replace(/Ö/g, "O").replace(/Ş/g, "S").replace(/Ü/g, "U")
                )}
                placeholder="SIFIRLA"
                className="rounded-none border-2 border-[#FF3B30] font-mono uppercase focus-visible:ring-0"
                data-testid="reset-confirm-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => setResetOpen(false)}>
              İptal
            </Button>
            <Button
              className="rounded-none bg-[#FF3B30] hover:bg-[#c9251c] text-white disabled:opacity-40"
              onClick={doReset}
              disabled={resetConfirm !== "SIFIRLA" || resetBusy}
              data-testid="reset-confirm-btn"
            >
              <Trash2 size={16} className="mr-2" />
              {resetBusy ? "Siliniyor…" : "Kalıcı Olarak Sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fullResetOpen} onOpenChange={setFullResetOpen}>
        <DialogContent className="rounded-none border-2 border-[#7C0002] bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2 text-[#7C0002]">
              <AlertTriangle size={22} /> NÜKLEER SIFIRLAMA
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">
              Silinecek koleksiyonlar: <span className="font-mono">users, customers, products, quotes, orders, waybills, settings, counters</span>.
            </p>
            <p className="text-sm text-[#7C0002]">
              <strong>Firma bilgilerin, logon, PDF şablonun, tüm kullanıcı hesapların (senin dahil) silinecek.</strong>
              Sonra 5 varsayılan hesap (admin, satış, depo, üretim, muhasebe) varsayılan şifreleriyle yeniden oluşturulacak.
              Bu diyalog kapandığında sayfadan atılıp giriş ekranına yönlendirileceksin.
            </p>
            <div>
              <Label className="text-xs font-mono uppercase">
                Onaylamak için <span className="text-[#7C0002] font-bold">SIFIRLA HEPSI</span> yazın
              </Label>
              <Input
                value={fullResetConfirm}
                onChange={(e) => setFullResetConfirm(
                  e.target.value.toLocaleUpperCase("tr-TR").replace(/İ/g, "I").replace(/Ç/g, "C").replace(/Ğ/g, "G").replace(/Ö/g, "O").replace(/Ş/g, "S").replace(/Ü/g, "U")
                )}
                placeholder="SIFIRLA HEPSI"
                className="rounded-none border-2 border-[#7C0002] font-mono uppercase focus-visible:ring-0"
                data-testid="full-reset-confirm-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none border-2 border-[#09090B]" onClick={() => setFullResetOpen(false)}>
              İptal
            </Button>
            <Button
              className="rounded-none bg-[#7C0002] hover:bg-[#4d0001] text-white disabled:opacity-40 font-bold"
              onClick={doFullReset}
              disabled={fullResetConfirm !== "SIFIRLA HEPSI" || fullResetBusy}
              data-testid="full-reset-confirm-btn"
            >
              <AlertTriangle size={16} className="mr-2" />
              {fullResetBusy ? "Sıfırlanıyor…" : "TÜMÜNÜ SİL VE SIFIRLA"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent className="rounded-none border-2 border-[#0055FF] bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight flex items-center gap-2 text-[#0055FF]">
              <DatabaseBackup size={22} /> Sistemi Geri Yükle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm">
              Seçilen yedek: <span className="font-mono">{restoreFile?.name || "—"}</span>
              {restoreFile && (
                <span className="text-neutral-500"> · {(restoreFile.size / 1024).toFixed(1)} KB</span>
              )}
            </p>
            <p className="text-sm text-[#FF3B30]">
              Bu işlem <strong>mevcut tüm verinin üzerine yazar</strong>. Kullanıcılar, ürünler, müşteriler,
              teklifler, siparişler, irsaliyeler ve ayarlar yedekteki hâliyle değiştirilecek. İşlem geri alınamaz.
            </p>
            <div>
              <Label className="text-xs font-mono uppercase">
                Onaylamak için <span className="text-[#0055FF] font-bold">GERI YUKLE</span> yazın
              </Label>
              <Input
                value={restoreConfirm}
                onChange={(e) => setRestoreConfirm(e.target.value.toUpperCase())}
                placeholder="GERI YUKLE"
                className="rounded-none border-2 border-[#0055FF] font-mono uppercase focus-visible:ring-0"
                data-testid="restore-confirm-input"
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              className="rounded-none border-2 border-[#09090B]"
              onClick={() => { setRestoreOpen(false); setRestoreFile(null); }}
              disabled={restoreBusy}
            >
              İptal
            </Button>
            <Button
              className="rounded-none bg-[#0055FF] hover:bg-[#003fd0] text-white disabled:opacity-40"
              onClick={doRestore}
              disabled={restoreConfirm !== "GERI YUKLE" || restoreBusy}
              data-testid="restore-confirm-btn"
            >
              <DatabaseBackup size={16} className="mr-2" />
              {restoreBusy ? "Geri yükleniyor…" : "Geri Yükle"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function WaybillXlsxColumnsEditor() {
  const [available, setAvailable] = React.useState([]);
  const [selected, setSelected] = React.useState([]);
  const [options, setOptions] = React.useState({
    include_logo: true, include_company_name: true, include_company_info: true,
    include_customer_block: true, include_totals_row: true, title: "İRSALİYE",
  });
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/waybills/xlsx-columns");
        setAvailable(data.available);
        setSelected(data.selected);
        if (data.options) setOptions((o) => ({ ...o, ...data.options }));
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    })();
  }, []);
  const toggle = (k) => setSelected((s) => s.includes(k) ? s.filter((x) => x !== k) : [...s, k]);
  const move = (idx, dir) => {
    setSelected((s) => {
      const next = [...s];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return s;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
  };
  const save = async () => {
    if (selected.length === 0) { toast.error("En az bir kolon seçin"); return; }
    setBusy(true);
    try {
      await api.put("/waybills/xlsx-columns", { columns: selected, options });
      toast.success(`Şablon kaydedildi (${selected.length} kolon)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const availableMap = Object.fromEntries(available.map((a) => [a.key, a.label]));
  const unpicked = available.filter((a) => !selected.includes(a.key));

  return (
    <div className="space-y-5">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Excel Başlık İçeriği</div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {[
            ["include_logo","Logo"], ["include_company_name","Şirket Adı"],
            ["include_company_info","Adres · Tel · VKN"], ["include_customer_block","Müşteri Blok"],
            ["include_totals_row","Toplam Satırı"],
          ].map(([k,label]) => (
            <label key={k} className={`flex items-center gap-2 border-2 border-[#09090B] p-2 cursor-pointer ${options[k] ? "bg-[#00A650]/10" : "bg-white"}`}
              data-testid={`waybill-xlsx-opt-${k}`}>
              <input type="checkbox" checked={!!options[k]} onChange={(e) => setOptions({...options, [k]: e.target.checked})}
                className="w-4 h-4 accent-[#00A650]" />
              <span className="text-sm">{label}</span>
            </label>
          ))}
        </div>
        <div className="mt-2">
          <Label className="text-xs font-mono uppercase">Başlık Metni</Label>
          <Input value={options.title} onChange={(e) => setOptions({...options, title: e.target.value})}
            className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 max-w-sm"
            data-testid="waybill-xlsx-title" />
        </div>
      </div>

      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Seçili Kolonlar (Sıralı)</div>
        {selected.length === 0 ? (
          <div className="text-xs font-mono text-neutral-500 italic">Henüz kolon seçilmedi.</div>
        ) : (
          <ul className="divide-y border-2 border-[#09090B]" data-testid="waybill-xlsx-selected-list">
            {selected.map((k, idx) => (
              <li key={k} className="flex items-center gap-2 p-2" data-testid={`waybill-xlsx-selected-${k}`}>
                <span className="font-mono text-xs text-neutral-500 w-6">{idx + 1}.</span>
                <span className="flex-1 text-sm">{availableMap[k] || k}</span>
                <Button type="button" size="sm" variant="ghost" className="rounded-none h-7 px-2"
                  onClick={() => move(idx, -1)} disabled={idx === 0} data-testid={`waybill-xlsx-up-${k}`}>↑</Button>
                <Button type="button" size="sm" variant="ghost" className="rounded-none h-7 px-2"
                  onClick={() => move(idx, +1)} disabled={idx === selected.length - 1} data-testid={`waybill-xlsx-down-${k}`}>↓</Button>
                <Button type="button" size="sm" variant="ghost" className="rounded-none h-7 px-2 text-[#FF3B30]"
                  onClick={() => toggle(k)} data-testid={`waybill-xlsx-remove-${k}`}>
                  <Trash2 size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-neutral-500 mb-2">Ekle</div>
        {unpicked.length === 0 ? (
          <div className="text-xs font-mono text-neutral-500 italic">Tüm kolonlar seçildi.</div>
        ) : (
          <div className="flex flex-wrap gap-2" data-testid="waybill-xlsx-available">
            {unpicked.map((col) => (
              <Button key={col.key} type="button" variant="outline" size="sm"
                className="rounded-none border-2 border-[#09090B]"
                onClick={() => toggle(col.key)} data-testid={`waybill-xlsx-add-${col.key}`}>
                + {col.label}
              </Button>
            ))}
          </div>
        )}
      </div>

      <Button className="rounded-none bg-[#09090B] hover:bg-[#00A650] text-white"
        onClick={save} disabled={busy} data-testid="waybill-xlsx-save-btn">
        <Save size={16} className="mr-2" />{busy ? "Kaydediliyor…" : "Şablonu Kaydet"}
      </Button>
    </div>
  );
}

function PaymentOptionsEditor({ items, onChange }) {
  const [draft, setDraft] = React.useState("");
  const list = items || [];
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    if (list.some((x) => x.toLocaleLowerCase("tr-TR") === v.toLocaleLowerCase("tr-TR"))) {
      toast.error("Bu seçenek zaten var");
      return;
    }
    onChange([...list, v]);
    setDraft("");
  };
  const remove = (idx) => onChange(list.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const next = [...list];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    onChange(next);
  };
  return (
    <div className="space-y-3">
      <div className="flex gap-2">
        <Input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Örn. Peşin, 30 gün vadeli, Havale (Garanti)…"
          className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
          data-testid="payment-option-input"
        />
        <Button
          type="button"
          onClick={add}
          className="rounded-none bg-[#09090B] hover:bg-[#0055FF]"
          data-testid="payment-option-add-btn"
        >
          <Save size={14} className="mr-2" /> Ekle
        </Button>
      </div>
      {list.length === 0 ? (
        <div className="text-xs font-mono text-neutral-500 italic">Henüz seçenek eklenmedi.</div>
      ) : (
        <ul className="divide-y border-2 border-[#09090B]" data-testid="payment-option-list">
          {list.map((it, idx) => (
            <li key={idx} className="flex items-center gap-2 p-2" data-testid={`payment-option-row-${idx}`}>
              <span className="font-mono text-xs text-neutral-500 w-6">{idx + 1}.</span>
              <span className="flex-1 text-sm">{it}</span>
              <Button type="button" variant="ghost" size="sm" className="rounded-none h-7 px-2" onClick={() => move(idx, -1)} disabled={idx === 0} title="Yukarı">↑</Button>
              <Button type="button" variant="ghost" size="sm" className="rounded-none h-7 px-2" onClick={() => move(idx, +1)} disabled={idx === list.length - 1} title="Aşağı">↓</Button>
              <Button
                type="button" variant="ghost" size="sm"
                className="rounded-none h-7 px-2 text-[#FF3B30]"
                onClick={() => remove(idx)}
                data-testid={`payment-option-remove-${idx}`}
                title="Sil"
              >
                <Trash2 size={14} />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AutoDailyReportBlock() {
  const [state, setState] = React.useState({
    emails: [], hour: 18, enabled: false, detailed: true,
    last_sent_at: null, last_status: "",
  });
  const [draftEmail, setDraftEmail] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [sendingNow, setSendingNow] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/daily-report-schedule");
        setState({ ...data, emails: data.emails || [] });
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    })();
  }, []);

  const addEmail = () => {
    const v = (draftEmail || "").trim().toLowerCase();
    if (!v) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      toast.error("Geçersiz e-posta");
      return;
    }
    if (state.emails.includes(v)) {
      toast.error("Bu adres zaten listede");
      return;
    }
    setState((s) => ({ ...s, emails: [...s.emails, v] }));
    setDraftEmail("");
  };
  const removeEmail = (idx) => setState((s) => ({ ...s, emails: s.emails.filter((_, i) => i !== idx) }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/daily-report-schedule", {
        emails: state.emails, hour: Number(state.hour),
        enabled: state.enabled, detailed: state.detailed,
      });
      toast.success(`Zamanlama kaydedildi (${state.emails.length} alıcı)`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    setSendingNow(true);
    try {
      const { data } = await api.post("/settings/daily-report-schedule/send-now");
      toast.success(`Test gönderim: ${data.status}`);
      const { data: refreshed } = await api.get("/settings/daily-report-schedule");
      setState({ ...refreshed, emails: refreshed.emails || [] });
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendingNow(false); }
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-xs font-mono uppercase">Alıcı E-posta Adresleri</Label>
        <div className="flex gap-2 mt-1">
          <Input
            type="email" value={draftEmail}
            onChange={(e) => setDraftEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
            placeholder="ornek@firma.com"
            className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
            data-testid="daily-report-email-input"
          />
          <Button
            type="button" onClick={addEmail}
            className="rounded-none bg-[#09090B] hover:bg-[#0055FF]"
            data-testid="daily-report-email-add-btn"
          >
            <Save size={14} className="mr-2" /> Ekle
          </Button>
        </div>
        {state.emails.length === 0 ? (
          <div className="mt-2 text-xs font-mono text-neutral-500 italic" data-testid="daily-report-emails-empty">
            Henüz alıcı eklenmedi. En az bir e-posta ekleyip Kaydet'e basın.
          </div>
        ) : (
          <ul className="mt-2 divide-y border-2 border-[#09090B]" data-testid="daily-report-emails-list">
            {state.emails.map((e, idx) => (
              <li key={e} className="flex items-center gap-2 p-2" data-testid={`daily-report-email-row-${idx}`}>
                <span className="font-mono text-xs text-neutral-500 w-6">{idx + 1}.</span>
                <span className="flex-1 text-sm font-mono truncate" title={e}>{e}</span>
                <Button
                  type="button" variant="ghost" size="sm"
                  className="rounded-none h-7 px-2 text-[#FF3B30]"
                  onClick={() => removeEmail(idx)}
                  data-testid={`daily-report-email-remove-${idx}`}
                  title="Sil"
                >
                  <Trash2 size={14} />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <Label className="text-xs font-mono uppercase">Saat (Türkiye · 0-23)</Label>
        <Input
          type="number" min={0} max={23} value={state.hour}
          onChange={(e) => setState({ ...state, hour: e.target.value })}
          className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 font-mono max-w-[120px]"
          data-testid="daily-report-hour-input"
        />
      </div>
      <label className="flex items-center gap-2 cursor-pointer" data-testid="daily-report-enabled-toggle">
        <input
          type="checkbox" checked={state.enabled}
          onChange={(e) => setState({ ...state, enabled: e.target.checked })}
          className="w-4 h-4 accent-[#0055FF]"
        />
        <span className="text-sm font-medium">Otomatik günlük rapor gönderimi aktif</span>
      </label>
      <label className="flex items-center gap-2 cursor-pointer" data-testid="daily-report-detailed-toggle">
        <input
          type="checkbox" checked={state.detailed}
          onChange={(e) => setState({ ...state, detailed: e.target.checked })}
          className="w-4 h-4 accent-[#0055FF]"
        />
        <span className="text-sm font-medium">Detaylı rapor (kalem satırları dâhil)</span>
      </label>
      <div className="flex flex-wrap gap-2">
        <Button
          className="rounded-none bg-[#0055FF] hover:bg-[#09090B] text-white"
          onClick={save} disabled={busy}
          data-testid="daily-report-save-btn"
        >
          <Save size={16} className="mr-2" />
          {busy ? "Kaydediliyor…" : "Zamanlamayı Kaydet"}
        </Button>
        <Button
          variant="outline"
          className="rounded-none border-2 border-[#09090B]"
          onClick={sendNow}
          disabled={sendingNow || state.emails.length === 0}
          data-testid="daily-report-send-now-btn"
        >
          {sendingNow ? "Gönderiliyor…" : `Şimdi Test Gönder (${state.emails.length})`}
        </Button>
      </div>
      {state.last_sent_at && (
        <div className="text-xs font-mono text-neutral-600 pt-2 border-t border-neutral-200">
          Son gönderim: <strong>{new Date(state.last_sent_at).toLocaleString("tr-TR")}</strong>
          {state.last_status && <> · Durum: <strong>{state.last_status}</strong></>}
        </div>
      )}
    </div>
  );
}

function AutoBackupBlock() {
  const [state, setState] = React.useState({ email: "", hour: 19, enabled: false, last_sent_at: null, last_status: "" });
  const [busy, setBusy] = React.useState(false);
  const [sendingNow, setSendingNow] = React.useState(false);

  React.useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/backup-schedule");
        setState(data);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    })();
  }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/settings/backup-schedule", {
        email: state.email, hour: Number(state.hour), enabled: state.enabled,
      });
      toast.success("Zamanlama kaydedildi");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sendNow = async () => {
    setSendingNow(true);
    try {
      const { data } = await api.post("/settings/backup-schedule/send-now");
      toast.success(`Test gönderim: ${data.status}`);
      const { data: refreshed } = await api.get("/settings/backup-schedule");
      setState(refreshed);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSendingNow(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-mono uppercase">Alıcı E-posta</Label>
          <Input
            type="email"
            value={state.email}
            onChange={(e) => setState({ ...state, email: e.target.value })}
            placeholder="ornek@firma.com"
            className="rounded-none border-2 border-[#09090B] focus-visible:ring-0"
            data-testid="backup-email-input"
          />
        </div>
        <div>
          <Label className="text-xs font-mono uppercase">Saat (Türkiye · 0-23)</Label>
          <Input
            type="number"
            min={0} max={23}
            value={state.hour}
            onChange={(e) => setState({ ...state, hour: e.target.value })}
            className="rounded-none border-2 border-[#09090B] focus-visible:ring-0 font-mono"
            data-testid="backup-hour-input"
          />
        </div>
      </div>
      <label className="flex items-center gap-2 cursor-pointer" data-testid="backup-enabled-toggle">
        <input
          type="checkbox"
          checked={state.enabled}
          onChange={(e) => setState({ ...state, enabled: e.target.checked })}
          className="w-4 h-4 accent-[#0055FF]"
        />
        <span className="text-sm font-medium">Otomatik günlük gönderim aktif</span>
      </label>
      <div className="flex flex-wrap gap-2">
        <Button
          className="rounded-none bg-[#0055FF] hover:bg-[#09090B] text-white"
          onClick={save}
          disabled={busy}
          data-testid="backup-schedule-save-btn"
        >
          <Save size={16} className="mr-2" />
          {busy ? "Kaydediliyor…" : "Zamanlamayı Kaydet"}
        </Button>
        <Button
          variant="outline"
          className="rounded-none border-2 border-[#09090B]"
          onClick={sendNow}
          disabled={sendingNow || !state.email}
          data-testid="backup-send-now-btn"
        >
          {sendingNow ? "Gönderiliyor…" : "Şimdi Test Gönder"}
        </Button>
      </div>
      {state.last_sent_at && (
        <div className="text-xs font-mono text-neutral-600 pt-2 border-t border-neutral-200">
          Son gönderim: <strong>{new Date(state.last_sent_at).toLocaleString("tr-TR")}</strong>
          {state.last_status && <> · Durum: <strong>{state.last_status}</strong></>}
        </div>
      )}
    </div>
  );
}
